import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { X, Save } from 'lucide-react';

interface CreateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  shopId: string;
  onProductCreated: () => void;
}

export default function CreateProductModal({ isOpen, onClose, shopId, onProductCreated }: CreateProductModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'EUR',
    image_url: '',
    is_available: true,
    stock_quantity: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      toast.error('Nome e prezzo sono obbligatori');
      return;
    }

    setLoading(true);
    try {
      const productData = {
        shop_id: shopId,
        name: formData.name,
        description: formData.description || null,
        price: parseFloat(formData.price),
        currency: formData.currency,
        image_url: formData.image_url || null,
        is_available: formData.is_available,
        stock_quantity: formData.stock_quantity ? parseInt(formData.stock_quantity) : null,
      };

      const { error } = await supabase
        .from('shop_products')
        .insert([productData]);

      if (error) throw error;

      toast.success('Prodotto creato con successo');
      setFormData({
        name: '',
        description: '',
        price: '',
        currency: 'EUR',
        image_url: '',
        is_available: true,
        stock_quantity: '',
      });
      onProductCreated();
      onClose();
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Errore nella creazione del prodotto');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      currency: 'EUR',
      image_url: '',
      is_available: true,
      stock_quantity: '',
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="glass border-white/20 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-white">Aggiungi Prodotto</DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="text-white hover:bg-white/10"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome Prodotto */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-white">
              Nome Prodotto *
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="es. Guinzaglio per cani"
              className="glass border-white/20 text-white"
              required
            />
          </div>

          {/* Descrizione */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-white">
              Descrizione
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrivi il tuo prodotto..."
              className="glass border-white/20 text-white resize-none"
              rows={3}
            />
          </div>

          {/* Prezzo */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price" className="text-white">
                Prezzo *
              </Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="19.99"
                className="glass border-white/20 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency" className="text-white">
                Valuta
              </Label>
              <Input
                id="currency"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="glass border-white/20 text-white"
                readOnly
              />
            </div>
          </div>

          {/* URL Immagine */}
          <div className="space-y-2">
            <Label htmlFor="image_url" className="text-white">
              URL Immagine
            </Label>
            <Input
              id="image_url"
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://esempio.com/immagine.jpg"
              className="glass border-white/20 text-white"
            />
          </div>

          {/* Quantità in Stock */}
          <div className="space-y-2">
            <Label htmlFor="stock_quantity" className="text-white">
              Quantità Disponibile
            </Label>
            <Input
              id="stock_quantity"
              type="number"
              value={formData.stock_quantity}
              onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
              placeholder="10"
              className="glass border-white/20 text-white"
            />
            <p className="text-xs text-muted-foreground">
              Lascia vuoto per quantità illimitata
            </p>
          </div>

          {/* Disponibile */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-white">Prodotto Disponibile</Label>
              <p className="text-sm text-muted-foreground">
                Il prodotto è attualmente in vendita
              </p>
            </div>
            <Switch
              checked={formData.is_available}
              onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={loading}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Creazione...' : 'Crea Prodotto'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}